import React, { useState } from 'react';
import { ArrowLeft, Info, AlertTriangle } from 'lucide-react';
import { CryptoList } from './CryptoList';
import { CryptoDetails } from './CryptoDetails';
import { cryptoCategories } from '../../data/cryptoCategories';
import type { CryptoRecommendation } from '../../types/crypto';

interface CryptoLibraryProps {
  onBack: () => void;
}

export function CryptoLibrary({ onBack }: CryptoLibraryProps) {
  const [selectedCrypto, setSelectedCrypto] = useState<CryptoRecommendation | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  return (
    <div className="min-h-screen bg-amber-900 text-amber-100 p-8 bg-[url('https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&q=80')] bg-cover bg-center bg-blend-overlay">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Kingdom
        </button>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold font-serif mb-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-transparent bg-clip-text">
            Royal Treasury Ledger
          </h1>
          <p className="text-xl text-amber-200 font-serif">
            Ancient wisdom for modern treasures
          </p>
        </div>

        {/* Risk Categories */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {cryptoCategories.map((category) => (
            <div key={category.name} className="bg-gray-900 rounded-lg p-4 border border-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className={`w-5 h-5 ${
                  category.riskLevel === 'Very High' ? 'text-red-400' :
                  category.riskLevel === 'High' ? 'text-orange-400' :
                  'text-yellow-400'
                }`} />
                <h3 className="font-bold text-white">{category.name}</h3>
              </div>
              <p className="text-sm text-gray-400 mb-2">{category.description}</p>
              <div className="text-xs text-red-400">{category.warning}</div>
            </div>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-900 rounded-lg p-1 flex">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              All
            </button>
            {cryptoCategories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === category.name
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {selectedCrypto ? (
          <CryptoDetails
            crypto={selectedCrypto}
            onBack={() => setSelectedCrypto(null)}
          />
        ) : (
          <CryptoList
            selectedCategory={selectedCategory}
            onSelectCrypto={setSelectedCrypto}
          />
        )}
      </div>
    </div>
  );
}